import { useTranslation } from 'react-i18next';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function UniversitySolutionPage() {
  const { t } = useTranslation();

  useSEO({
    title: '대학 솔루션 - KR STUDIO | 고등교육 맞춤형 이러닝',
    description: 'KR스튜디오의 대학 특화 솔루션으로 최고 수준의 온라인 강의를 제공하세요. 대학 교육 환경에 최적화된 콘텐츠 제작 서비스입니다.',
    keywords: '대학 솔루션, 고등교육, 온라인 강의, 대학 이러닝, 교육 콘텐츠',
    canonical: '/university-solution',
    schema: createServiceSchema(
      '대학 솔루션',
      '대학 교육 환경에 최적화된 고등교육 맞춤형 콘텐츠 제작 서비스'
    ),
  });

  const cases = [
    {
      university: 'A대학교',
      image: 'https://public.readdy.ai/ai/img_res/f2a3a1cec87c2f172cbf693a1193ab32.jpg',
      challenge: '100개 이상의 온라인 강좌 제작 필요',
      solution: '전용 스튜디오 구축 및 제작 시스템 구축',
      results: ['연간 150개 강좌 제작', 'LMS 자동 연동', '제작 비용 40% 절감']
    },
    {
      university: 'B대학교',
      image: 'https://readdy.ai/api/search-image?query=University%20live%20streaming%20lecture%20hall%20with%20multiple%20cameras%2C%20students%20attending%20online%2C%20professor%20presenting%2C%20modern%20educational%20technology%2C%20clean%20academic%20atmosphere&width=800&height=600&seq=uni2&orientation=landscape',
      challenge: '실시간 온라인 수업 시스템 구축',
      solution: '강의실 라이브 스트리밍 시스템 설치',
      results: ['30개 강의실 동시 송출', '1,000명 동시 접속', '출석 자동 체크']
    },
    {
      university: 'C대학교',
      image: 'https://readdy.ai/api/search-image?query=University%20content%20production%20center%20with%20multiple%20recording%20booths%2C%20faculty%20creating%20educational%20content%2C%20modern%20e-learning%20facility%2C%20clean%20professional%20academic%20environment&width=800&height=600&seq=uni3&orientation=landscape',
      challenge: '교수 자가 제작 콘텐츠 품질 향상',
      solution: '셀프 스튜디오 및 교육 프로그램 제공',
      results: ['교수 만족도 95%', '콘텐츠 품질 향상', '제작 시간 단축']
    }
  ];

  const services = [
    {
      icon: 'ri-building-4-line',
      title: '스튜디오 구축',
      description: '교내 전용 이러닝 스튜디오 설계 및 구축',
      features: ['공간 설계', '장비 설치', '시스템 통합', '운영 교육']
    },
    {
      icon: 'ri-film-line',
      title: '콘텐츠 제작',
      description: '전문 제작팀의 고품질 강의 콘텐츠 제작',
      features: ['촬영 및 편집', '자막 제작', 'SCORM 패키징', '품질 관리']
    },
    {
      icon: 'ri-live-line',
      title: '실시간 강의',
      description: '안정적인 라이브 스트리밍 시스템',
      features: ['멀티캠 송출', '양방향 소통', '녹화 자동화', '출석 연동']
    },
    {
      icon: 'ri-database-2-line',
      title: 'LMS 연동',
      description: '학습관리시스템 완벽 통합',
      features: ['자동 업로드', '진도 관리', '평가 연동', '데이터 분석']
    }
  ];

  const features = [
    { title: '학사 일정 연동', description: '학기 일정에 맞춘 제작 스케줄', icon: 'ri-calendar-line' },
    { title: '교수 지원', description: '촬영부터 편집까지 전담 지원', icon: 'ri-user-star-line' },
    { title: '품질 관리', description: '교육 콘텐츠 품질 기준 준수', icon: 'ri-shield-check-line' },
    { title: '저작권 관리', description: '콘텐츠 저작권 보호 시스템', icon: 'ri-copyright-line' },
    { title: '접근성 지원', description: '자막, 수어 등 접근성 강화', icon: 'ri-wheelchair-line' },
    { title: '데이터 분석', description: '학습 데이터 분석 및 리포트', icon: 'ri-bar-chart-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-indigo-800 to-gray-900 text-white py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20university%20campus%20with%20digital%20learning%20technology%2C%20students%20using%20online%20education%20platform%2C%20innovative%20academic%20environment%2C%20clean%20professional%20educational%20setting&width=1920&height=800&seq=unihero&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-black mb-6">대학 솔루션 사례</h1>
            <p className="text-2xl mb-8 text-gray-300">
              대학 교육 혁신을 위한<br />
              맞춤형 이러닝 솔루션
            </p>
            <a href="mailto:contact@krstudio.com" className="inline-block bg-blue-500 text-white px-12 py-4 text-lg font-bold hover:bg-opacity-90 transition cursor-pointer whitespace-nowrap hidden">
              대학 솔루션 문의
            </a>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">도입 사례</h2>
            <p className="text-xl text-gray-600">주요 대학의 성공 사례</p>
          </div>
          <div className="space-y-16">
            {cases.map((item, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
                <div className="lg:w-1/2">
                  <div className="relative aspect-[16/10] overflow-hidden shadow-xl">
                    <img 
                      src={item.image} 
                      alt={item.university}
                      className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <h3 className="text-3xl font-black mb-4">{item.university}</h3>
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-blue-600 mb-2">과제</h4>
                    <p className="text-gray-700">{item.challenge}</p>
                  </div>
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-blue-600 mb-2">솔루션</h4>
                    <p className="text-gray-700">{item.solution}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-blue-600 mb-3">성과</h4>
                    <ul className="space-y-2">
                      {item.results.map((result, idx) => (
                        <li key={idx} className="flex items-center">
                          <i className="ri-checkbox-circle-fill text-blue-500 mr-3"></i>
                          <span className="font-bold text-gray-700">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">제공 서비스</h2>
            <p className="text-xl text-gray-600">대학 맞춤형 통합 서비스</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-8 hover:shadow-xl transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-blue-500/10 flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-all">
                  <i className={`${service.icon} text-3xl text-blue-500 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-blue-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">특화 기능</h2>
            <p className="text-xl text-gray-600">대학 교육에 최적화된 기능</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-14 h-14 bg-blue-500/10 flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl text-blue-500`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">대학 솔루션 실적</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-black text-blue-500 mb-3">20+</div>
              <div className="text-xl">파트너 대학</div>
            </div>
            <div>
              <div className="text-5xl font-black text-blue-500 mb-3">5,000+</div>
              <div className="text-xl">제작 강좌</div>
            </div>
            <div>
              <div className="text-5xl font-black text-blue-500 mb-3">100K+</div>
              <div className="text-xl">수강 학생</div>
            </div>
            <div>
              <div className="text-5xl font-black text-blue-500 mb-3">95%</div>
              <div className="text-xl">만족도</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6">대학 교육 혁신을 시작하세요</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            검증된 솔루션으로 귀 대학의 온라인 교육을 한 단계 업그레이드하세요
          </p>
          <a href="mailto:contact@krstudio.com" className="inline-block bg-blue-500 text-white px-12 py-4 text-lg font-bold hover:bg-opacity-90 transition cursor-pointer whitespace-nowrap hidden">
            무료 컨설팅 신청
          </a>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}